import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../../lib/feathers';
import { plateUrl } from '../../../components/data-management/PlateCropEditor';

// Every grape, sorted by how many of our wineries make it, so the pages worth
// writing come first. A grape page is public only once published; nothing here
// publishes by itself. See docs/VARIETAL_PAGES_PLAN.md.

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const PAGE = 50;
const FILTERS = [
  { key: 'published', label: 'Published', options: [['', 'All'], ['true', 'Published'], ['false', 'Not published']] },
  { key: 'hasImage', label: 'Image', options: [['', 'All'], ['true', 'Has an image'], ['false', 'No image']] },
  { key: 'hasVivc', label: 'VIVC', options: [['', 'All'], ['true', 'Linked'], ['false', 'Not linked']] },
];

export default function Grapes() {
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ published: '', hasImage: '', hasVivc: '' });
  const [sort, setSort] = useState('wineries');
  const [skip, setSkip] = useState(0);
  const [result, setResult] = useState({ total: 0, data: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const query = { $limit: PAGE, $skip: skip, sort };
        if (q.trim()) query.q = q.trim();
        Object.entries(filters).forEach(([k, v]) => { if (v) query[k] = v; });
        const res = await client.service('admin/grape-overview').find({ query });
        if (!cancelled) setResult(res);
      } catch (e) {
        console.log('error loading grapes', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, filters, sort, skip]);

  const thumb = (path) => (path
    ? <img src={plateUrl(path, 160)} alt="" className="h-16 w-12 rounded object-cover" />
    : <div className="h-16 w-12 rounded bg-gray-100" />);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="grape-q" className="block text-xs font-medium text-gray-500">Search</label>
          <input id="grape-q" type="search" value={q} placeholder="Grape name"
            onChange={(e) => { setQ(e.target.value); setSkip(0); }}
            className="mt-1 rounded-md border-0 py-1.5 px-3 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary" />
        </div>
        {FILTERS.map((f) => (
          <div key={f.key}>
            <label htmlFor={`grape-${f.key}`} className="block text-xs font-medium text-gray-500">{f.label}</label>
            <select id={`grape-${f.key}`} value={filters[f.key]}
              onChange={(e) => { setFilters({ ...filters, [f.key]: e.target.value }); setSkip(0); }}
              className="mt-1 rounded-md border-0 py-1.5 pl-3 pr-8 text-sm ring-1 ring-inset ring-gray-300">
              {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        ))}
        <div>
          <label htmlFor="grape-sort" className="block text-xs font-medium text-gray-500">Sort</label>
          <select id="grape-sort" value={sort} onChange={(e) => { setSort(e.target.value); setSkip(0); }}
            className="mt-1 rounded-md border-0 py-1.5 pl-3 pr-8 text-sm ring-1 ring-inset ring-gray-300">
            <option value="wineries">Most wineries</option>
            <option value="name">Name</option>
          </select>
        </div>
        <p className="ml-auto text-sm text-gray-500">{loading ? 'Loading...' : `${result.total} grapes`}</p>
      </div>

      <div className="overflow-x-auto rounded-lg shadow ring-1 ring-black ring-opacity-5">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={TH}> </th>
              <th scope="col" className={TH}>Grape</th>
              <th scope="col" className={`${TH} text-right`}>Wineries</th>
              <th scope="col" className={`${TH} text-right`}>Images</th>
              <th scope="col" className={`${TH} text-right`}>Names</th>
              <th scope="col" className={TH}>Copy</th>
              <th scope="col" className={TH}>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {result.data.map((g) => (
              <tr key={g.id} className="hover:bg-gray-50">
                <td className="px-4 py-2">{thumb(g.primaryPath)}</td>
                <td className="px-4 py-2">
                  <Link to={`/admin/data-management/grapes/${g.id}/edit`} className="font-medium text-gray-900 hover:text-primary">
                    {g.name}
                  </Link>
                  <div className="text-xs text-gray-500">
                    {[g.familyName && `${g.familyName} family`, g.color, g.vivcId ? `VIVC ${g.vivcId}` : 'no VIVC link']
                      .filter(Boolean).join(' · ')}
                  </div>
                </td>
                <td className="px-4 py-2 text-right text-sm tabular-nums">{g.wineryCount}</td>
                <td className="px-4 py-2 text-right text-sm tabular-nums">{g.imageCount}</td>
                <td className="px-4 py-2 text-right text-sm tabular-nums">{g.nameCount}</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {[g.hasSummary && 'summary', g.hasDescription && 'description'].filter(Boolean).join(', ') || <span className="text-gray-400">none</span>}
                </td>
                <td className="px-4 py-2 text-sm">
                  {g.published
                    ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Published</span>
                    : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Draft</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <button type="button" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
          className="rounded-md px-3 py-1.5 ring-1 ring-gray-300 disabled:opacity-40">Previous</button>
        <span className="text-gray-500">
          {result.total ? `${skip + 1} to ${Math.min(skip + PAGE, result.total)} of ${result.total}` : ''}
        </span>
        <button type="button" disabled={skip + PAGE >= result.total} onClick={() => setSkip(skip + PAGE)}
          className="rounded-md px-3 py-1.5 ring-1 ring-gray-300 disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}
